import * as React from 'react';
import { useEffect } from 'react';

import { PropsOf } from '@emotion/react';
import styled from '@emotion/styled';
import { Box, Flex } from '@opendoor/bricks/core';

import { CrossFadeAnimation } from 'src/components/flowPage/twoColumn/CrossFadeAnimation';
import Footer from 'src/components/flowPage/twoColumn/Footer';
import { showPrompt } from 'src/helpers/showPrompt';
import { useAppSelector } from 'src/store/hooks';
import { getInApp } from 'src/store/router/selectors';

import SaveProgress from '../sellerFlow/SaveProgress';
import { ContentContainer, PageHeader, PageLayout } from './PageLayout';

const Main = styled.main`
  /* fill the page, but leave some room for the gradient on the bottom bar */
  /* for safari 15.4 */
  min-height: calc(100vh - 112px);
  /* for everyone else  */
  min-height: calc(100dvh - 112px);

  @media (max-width: 921px) {
    margin-top: 24px;
  }
  @media (min-width: 922px) {
    margin-top: 48px;
  }
`;

export const TwoColumnPageContent: React.FC<React.PropsWithChildren<{ showFooter?: boolean }>> = ({
  children,
  showFooter = true,
}) => {
  const displayFooter = !useAppSelector(getInApp) && showFooter;
  return (
    <Box
      as={Main}
      backgroundColor="neutrals0"
      flexGrow={1}
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
    >
      <ContentContainer>{children}</ContentContainer>
      <Box data-testid="reception-footer" mt="48px" mb={['140px', null, null, '156px']}>
        {/* this is hidden only when we're in the app */}
        {displayFooter && <Footer />}
      </Box>
    </Box>
  );
};

export const Layout: React.FC<React.PropsWithChildren<{ showFooter?: boolean }>> = ({
  children,
  showFooter = true,
}) => {
  return (
    <PageLayout>
      <PageHeader>
        <SaveProgress />
      </PageHeader>
      <TwoColumnPageContent showFooter={showFooter}>{children}</TwoColumnPageContent>
    </PageLayout>
  );
};

// current designs have this split happening at 922px - which is in the middle
// of our 3rd break point
// for now - let's use a custom container
export const ColumnContainer = styled(Flex)`
  margin: 0 auto;
  @media (max-width: 921px) {
    flex-direction: column;
    gap: 48px;
  }
  @media (min-width: 922px) {
    flex-direction: row;
    gap: 64px;
  }
`;

export const Column = (props: PropsOf<typeof Box>) => <Box flex={1} flexBasis={0} {...props} />;

type TwoColumnPageLayoutProps = {
  slug: string;
  leftColumnPageContent: React.ReactNode;
  rightColumnPageContent: React.ReactNode;
  navigationComponent: React.ReactNode;
  formProps?: FormProps;
};

type FormProps = {
  isForm: boolean;
  handleSubmit?: (e: React.FormEvent<HTMLFormElement>) => void;
};

const TwoColumnPageLayout: React.FC<TwoColumnPageLayoutProps> = ({
  slug,
  leftColumnPageContent,
  rightColumnPageContent,
  navigationComponent,
  formProps = { isForm: false },
}) => {
  const atEnd = useAppSelector((state) => state.flow_ng.atEnd);

  useEffect(() => {
    if (atEnd) {
      window.removeEventListener('beforeunload', showPrompt);
    } else {
      window.addEventListener('beforeunload', showPrompt);
    }

    // if we unload this page (because we sent them to an end of flow page
    // within reception) go ahead and remove the event
    return () => {
      window.removeEventListener('beforeunload', showPrompt);
    };
  }, [atEnd]);

  return (
    <Layout>
      <form
        method={formProps.isForm ? 'post' : undefined}
        onSubmit={formProps.isForm ? formProps.handleSubmit : undefined}
      >
        <ColumnContainer>
          {/* Left Column */}
          <Column>
            <CrossFadeAnimation watchValue={slug}>{leftColumnPageContent}</CrossFadeAnimation>
          </Column>
          {/* Right Column */}
          <Column>
            <CrossFadeAnimation watchValue={slug}>{rightColumnPageContent}</CrossFadeAnimation>
          </Column>
        </ColumnContainer>

        {navigationComponent}
      </form>
    </Layout>
  );
};

export default TwoColumnPageLayout;
